import axios from 'axios';
import { createNote, uploadImageAsNote, getNoteAttachment } from './noteService';

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";
const MODULE_NAME = "edu_edu_details";

// Fields exposed in the Education UI and available in the SuiteCRM module
const EDUCATION_MODULE_FIELDS = [
  "id", // Required for edit operations
  "board",
  "course",
  "currentlystudying_c",
  "date_entered",
  "date_modified",
  "degreetype",
  "deleted",
  "description",
  "educationtype",
  "endyear_c",
  "grade",
  "instituecity",
  "institute",
  "institutecountry",
  "institutepincode",
  "institutestate",
  "institutetype",
  "name",
  "others_c", // Added for "Others" qualification field
  "parentid",
  "qualification",
  "startyear_c",
  "image",
];

// Map UI field aliases to SuiteCRM fields where names differ
const UI_FIELD_ALIASES = {
  instituteschool: "institute",
};

// Filter outgoing payload to include only module fields used in the UI
const filterEducationPayload = (data = {}) => {
  const payload = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (normalizedValue === "") {
      // allow empty strings for explicit clearing only when field explicitly present
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        return;
      }
    }

    if (
      key === "image" &&
      typeof normalizedValue === "string" &&
      normalizedValue.startsWith("data:")
    ) {
      return;
    }

    if (EDUCATION_MODULE_FIELDS.includes(key)) {
      payload[key] = normalizedValue;
      return;
    }

    const mappedField = UI_FIELD_ALIASES[key];
    if (mappedField && EDUCATION_MODULE_FIELDS.includes(mappedField)) {
      payload[mappedField] = normalizedValue;
    }
  });

  return payload;
};

// Helper function to process education data entries
const processEducationData = async (entryList, sessionId) => {
  if (!Array.isArray(entryList)) return [];

  const processed = entryList.map((entry) => {
    const values = Object.fromEntries(
      Object.values(entry.name_value_list || {}).map((field) => [field.name, field.value])
    );

    const filteredValues = {};
    EDUCATION_MODULE_FIELDS.forEach((field) => {
      if (values[field] !== undefined && values[field] !== null) {
        filteredValues[field] = values[field];
      }
    });

    // Ensure alias fields are also surfaced for UI convenience
    Object.entries(UI_FIELD_ALIASES).forEach(([uiField, moduleField]) => {
      if (filteredValues[moduleField] !== undefined) {
        filteredValues[uiField] = filteredValues[moduleField];
      }
    });

    return {
      id: entry.id,
      ...filteredValues,
    };
  });

  if (sessionId) {
    for (const entry of processed) {
      if (entry.image && typeof entry.image === "string" && !entry.image.startsWith("data:")) {
        try {
          const imageData = await getNoteAttachment(sessionId, entry.image);
          if (imageData) {
            entry.image = imageData;
          }
        } catch (error) {
          console.error("Error fetching education image note attachment:", error);
        }
      }
    }
  }

  return processed;
};

const uploadEducationImage = async (sessionId, educationId, base64Image) => {
  if (!sessionId || !educationId || !base64Image || !base64Image.startsWith("data:")) {
    return null;
  }

  try {
    const noteName = "School/Institute ID";
    const noteId = await createNote(sessionId, MODULE_NAME, educationId, noteName);
    const filename = `education_id_${Date.now()}.jpg`;
    await uploadImageAsNote(sessionId, noteId, base64Image, filename, MODULE_NAME, educationId);
    return noteId;
  } catch (error) {
    console.error("Error uploading education image:", error);
    throw error;
  }
};

/**
 * Fetch all education data from Education module
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of education objects
 */
export const fetchEducationData = async (sessionId) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      select_fields: EDUCATION_MODULE_FIELDS,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    const entryList = response?.data?.entry_list || [];

    return await processEducationData(entryList, sessionId);
  } catch (error) {
    console.error('Error fetching education data:', error);
    throw new Error('Failed to fetch education data');
  }
};

/**
 * Fetch education entries for a specific parent/user
 * @param {string} sessionId
 * @param {string} parentId
 * @returns {Promise<Array>} - Array of education entries associated with the parent
 */
export const fetchEducationDetailsByParent = async (sessionId, parentId) => {
  if (!sessionId || !parentId) {
    return [];
  }

  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      query: `edu_edu_details.parentid = '${parentId}' AND edu_edu_details.deleted = 0`,
      order_by: "date_entered",
      offset: 0,
      select_fields: EDUCATION_MODULE_FIELDS,
      max_results: 200,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;
    
    const response = await axios.get(fullUrl);
    
    const entryList = response?.data?.entry_list || [];

    const processedData = await processEducationData(entryList, sessionId);
    
    return processedData;
  } catch (error) {
    console.error('❌ [EDUCATION SERVICE] Error fetching education data by parent:', error);
    console.error('❌ [EDUCATION SERVICE] Error details:', error.response?.data || error.message);
    return [];
  }
};

/**
 * Add new education entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {Object} data - Education data object
 * @returns {Promise<Object>} - Created entry data
 */
export const addEducationData = async (sessionId, data) => {
  try {
    const filteredPayload = filterEducationPayload(data);

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: Object.entries(filteredPayload).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    const createdId = response?.data?.id;

    if (createdId && data.image && typeof data.image === "string" && data.image.startsWith("data:")) {
      const noteId = await uploadEducationImage(sessionId, createdId, data.image);
      if (noteId) {
        const updatePayload = filterEducationPayload({ ...data, id: createdId, image: noteId });
        updatePayload.id = createdId;

        const updateRestData = JSON.stringify({
          session: sessionId,
          module_name: MODULE_NAME,
          name_value_list: Object.entries(updatePayload).map(([key, value]) => ({
            name: key,
            value,
          })),
        });

        const updateUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(updateRestData)}`;
        await axios.post(updateUrl);
      }
    }

    return response.data;
  } catch (error) {
    console.error('Error adding education data:', error);
    throw new Error('Failed to add education data');
  }
};

/**
 * Edit existing education entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to update
 * @param {Object} data - Updated education data object
 * @returns {Promise<Object>} - Updated entry data
 */
export const editEducationData = async (sessionId, id, data) => {
  try {
    // Include the id in the data object before filtering
    const dataWithId = { ...data, id };
    const updatedData = { ...dataWithId };

    if (data && typeof data.image === "string" && data.image.startsWith("data:")) {
      const noteId = await uploadEducationImage(sessionId, id, data.image);
      updatedData.image = noteId;
    }
    
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: Object.entries(filterEducationPayload(updatedData)).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    return response.data;
  } catch (error) {
    console.error('Error editing education data:', error);
    throw new Error('Failed to edit education data');
  }
};

/**
 * Delete education entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to delete
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteEducationData = async (sessionId, id) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: [
        { name: "id", value: id },
        { name: "deleted", value: 1 },
      ],
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);

    if (response?.data?.id !== id) {
      console.warn(`Unexpected delete response for education entry ${id}:`, response?.data);
    }

    return response.data;
  } catch (error) {
    console.error('Error deleting education data:', error);
    throw new Error('Failed to delete education data');
  }
};

/**
 * Synchronize education entries for a user with SuiteCRM
 * @param {string} sessionId
 * @param {string} parentId
 * @param {Array} entries - Education entries prepared from UI data
 */
export const syncEducationEntries = async (sessionId, parentId, entries = [], removedIds = []) => {
  if (!sessionId || !parentId) return;

  const sanitizedEntries = Array.isArray(entries) ? entries : [];
  const removalSet = new Set((removedIds || []).filter(Boolean));

  const existingEntries = await fetchEducationDetailsByParent(sessionId, parentId);
  const existingById = new Map(existingEntries.map((entry) => [entry.id, entry]));
  const existingByName = new Map(
    existingEntries
      .filter((entry) => entry.name)
      .map((entry) => [entry.name.toLowerCase(), entry])
  );

  const usedIds = new Set();

  for (const sourceEntry of sanitizedEntries) {
    if (!sourceEntry || typeof sourceEntry !== "object") continue;

    const prepared = {
      ...sourceEntry,
      parentid: parentId,
    };

    const filteredPayload = filterEducationPayload(prepared);

    const payloadKeys = Object.keys(filteredPayload);
    const hasData = payloadKeys.some(
      (key) => key !== "parentid" && filteredPayload[key] !== undefined && filteredPayload[key] !== null && filteredPayload[key] !== ""
    );
    if (!hasData) continue;

    const nameValueList = Object.entries(filteredPayload).map(([key, value]) => ({
      name: key,
      value: value,
    }));

    let targetId = sourceEntry.id || undefined;

    if (!targetId && sourceEntry.name) {
      const match = existingByName.get(sourceEntry.name.toLowerCase());
      if (match) {
        targetId = match.id;
      }
    }

    if (targetId && existingById.has(targetId)) {
      nameValueList.push({ name: "id", value: targetId });
    }

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: nameValueList,
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;
    const response = await axios.post(fullUrl);
    const savedId = response?.data?.id || targetId;

    if (savedId) {
      usedIds.add(savedId);
    }

    const shouldUploadImage =
      !!savedId &&
      typeof sourceEntry.image === "string" &&
      sourceEntry.image.startsWith("data:");

    if (shouldUploadImage) {
      try {
        const noteId = await uploadEducationImage(sessionId, savedId, sourceEntry.image);
        if (noteId) {
          const updateRestData = JSON.stringify({
            session: sessionId,
            module_name: MODULE_NAME,
            name_value_list: [
              { name: "id", value: savedId },
              { name: "image", value: noteId },
            ],
          });

          const updateUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(updateRestData)}`;
          await axios.post(updateUrl);
        }
      } catch (error) {
        console.error(`Failed to upload education image for entry ${savedId}:`, error);
      }
    }
  }

  // Soft delete entries that were removed in the UI
  const entriesToDelete = existingEntries.filter((entry) => {
    if (!entry.id) return false;
    if (removalSet.has(entry.id)) return true;
    return !usedIds.has(entry.id);
  });

  for (const entry of entriesToDelete) {
    try {
      await deleteEducationData(sessionId, entry.id);
    } catch (error) {
      console.error(`Failed to delete education entry ${entry.id}:`, error);
    }
  }
};
